import { parseDockerfile } from "../dockerfile";
import { ExpectedError } from "../expectedError";
import {
  VOLUME_NAME_RE,
  type VolumeAccess,
  type VolumeConcurrency,
  type VolumeDirective,
  type VolumeScope,
} from "./types";

type StepFile = { path: string; content: string };

export function parseVolumeDirective(line: string): VolumeDirective | null {
  const syntaxError = () =>
    new ExpectedError(
      `Invalid VOLUME syntax: ${line}. Expected VOLUME ["<name>[:ro|:scope=run|:access=ro|:concurrency=exclusive]"].`
    );
  const match = line.match(/^VOLUME\s+\["([^"]+)"\]$/);
  if (!match) throw syntaxError();
  const [name, rawOptions] = splitVolumeSpec(match[1]!);
  if (!VOLUME_NAME_RE.test(name)) {
    throw new ExpectedError(
      `Invalid volume name ${JSON.stringify(name)} in ${line}. Names must match ${VOLUME_NAME_RE}.`
    );
  }
  return { name, ...parseVolumeOptions(rawOptions, line) };
}

function splitVolumeSpec(spec: string): [string, string | undefined] {
  const colon = spec.indexOf(":");
  if (colon === -1) return [spec, undefined];
  return [spec.slice(0, colon), spec.slice(colon + 1)];
}

function parseVolumeOptions(
  rawOptions: string | undefined,
  line: string
): Omit<VolumeDirective, "name"> {
  let scope: VolumeScope = "branch";
  let access: VolumeAccess = "rw";
  let concurrency: VolumeConcurrency = "shared";
  if (!rawOptions) return { scope, access, concurrency };

  for (const option of rawOptions.split(",")) {
    if (option === "ro") {
      access = "ro";
      continue;
    }
    if (option === "rw") {
      access = "rw";
      continue;
    }
    const [key, value, extra] = option.split("=");
    if (extra !== undefined || !key || value === undefined) {
      throw new ExpectedError(
        `Invalid volume option ${JSON.stringify(option)} in ${line}.`
      );
    }
    if (key === "scope" && (value === "branch" || value === "run")) {
      scope = value;
      continue;
    }
    if (key === "access" && (value === "rw" || value === "ro")) {
      access = value;
      continue;
    }
    if (
      key === "concurrency" &&
      (value === "shared" || value === "exclusive")
    ) {
      concurrency = value;
      continue;
    }
    throw new ExpectedError(
      `Invalid volume option ${JSON.stringify(option)} in ${line}.`
    );
  }

  return { scope, access, concurrency };
}

export function extractVolumes(files: StepFile[]): VolumeDirective[] {
  const dockerfile = files.find((f) => f.path === "Dockerfile");
  if (!dockerfile) throw new Error("Step is missing a Dockerfile");
  const seen = new Set<string>();
  const volumes: VolumeDirective[] = [];
  for (const inst of parseDockerfile(dockerfile.content)) {
    if (inst.kind !== "volume") continue;
    if (seen.has(inst.volume.name)) {
      throw new ExpectedError(
        `Duplicate VOLUME declaration for ${JSON.stringify(inst.volume.name)}. Each volume name may appear at most once per step.`
      );
    }
    seen.add(inst.volume.name);
    volumes.push(inst.volume);
  }
  return volumes;
}
